import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getPaginationRowModel,
} from "@tanstack/react-table";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { INPUT_VARIANT } from "../InputElements/const";
import {
  StyledTable,
  PaginationControls,
  Pagination,
  PaginationInfo,
  PaginationButtonGroup,
  PaginationRowLimit,
  InputPageChange,
  NoDataFound,
  NoDataFound as Loading,
} from "./styles";
import Select from "../InputElements/Select";
import Button from "../Button";
import { generateArray } from "@/util/common_function";
import Loader from "../Loader";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { labels } from "@/util/label";

const columnHelper = createColumnHelper();

// Minimum width (px) a column can be resized down to via the Config
// drag-resize handle — mirrors the same floor used by common/elements/DataTable.
const MIN_COLUMN_WIDTH = 50;

// Drag-to-resize: dragging the divider between two ADJACENT columns trades
// width between them — grows one, shrinks the other by the exact same
// amount — so the table's total width never changes, matching normal
// spreadsheet-style column resize. Only a column with a column to its right
// gets a handle (there's nothing to trade with past the last column, so it
// resizes via the previous column's handle instead — the same physical
// divider). `thRefs` is the shared map of column key -> <th> DOM node
// (populated by each header cell's ref below), read here to get both
// columns' CURRENT rendered width at drag start, whether or not either has
// been explicitly resized yet.
const useAdjacentColumnResize = (thRefs, leftKey, rightKey, onResizePair) => {
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startLeftWidth = useRef(0);
  const startRightWidth = useRef(0);

  return (e) => {
    if (!rightKey) return;
    e.stopPropagation();
    e.preventDefault();

    isResizing.current = true;
    startX.current = e.clientX;
    startLeftWidth.current = thRefs.current[leftKey]?.offsetWidth ?? 100;
    startRightWidth.current = thRefs.current[rightKey]?.offsetWidth ?? 100;

    const onMouseMove = (moveEvent) => {
      if (!isResizing.current) return;
      let diff = moveEvent.clientX - startX.current;
      // Clamp so neither column drops below MIN_COLUMN_WIDTH — clamping the
      // diff itself (not each width separately) keeps both sides symmetric
      // regardless of drag direction.
      const minDiff = MIN_COLUMN_WIDTH - startLeftWidth.current;
      const maxDiff = startRightWidth.current - MIN_COLUMN_WIDTH;
      diff = Math.max(minDiff, Math.min(diff, maxDiff));
      onResizePair(startLeftWidth.current + diff, startRightWidth.current - diff);
    };

    const onMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
};

const ResizeHandle = ({ onMouseDown }) => (
  <span
    onMouseDown={onMouseDown}
    onPointerDown={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}
    style={{
      position: "absolute",
      right: 0,
      top: 0,
      bottom: 0,
      width: 6,
      cursor: "col-resize",
      userSelect: "none",
    }}
  />
);

// Rendered instead of a plain <th> only while `draggable` (i.e. "Config"
// mode) is active — combines @dnd-kit sortable (reorder) with the resize
// handle above. Keyed directly by the column's real `key` (this component
// renders its own <th> elements, so no synthetic drag-id indirection is
// needed).
const ConfigurableHeaderCell = ({
  id,
  width,
  isDragOver,
  children,
  thRefs,
  rightKey,
  onResizePair,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id });
  const registerRef = (node) => {
    thRefs.current[id] = node;
    setNodeRef(node);
  };
  const onResizeMouseDown = useAdjacentColumnResize(thRefs, id, rightKey, onResizePair);

  return (
    <th
      ref={registerRef}
      style={{
        position: "relative",
        cursor: "grab",
        width: width || undefined,
        minWidth: width || undefined,
        maxWidth: width || undefined,
        ...(isDragging ? { zIndex: 2, userSelect: "none" } : {}),
        ...(isDragOver ? { borderInlineStart: "1px dashed gray" } : {}),
      }}
      {...attributes}
      {...listeners}
    >
      {children}
      {rightKey && <ResizeHandle onMouseDown={onResizeMouseDown} />}
    </th>
  );
};

// Resize-only header cell: no `@dnd-kit`, no DndContext needed — renders
// whenever `resizable` is on but `draggable` (Config mode) isn't, so every
// grid can have its columns resized live without ever entering Config mode.
const ResizeOnlyHeaderCell = ({
  width,
  children,
  onClick,
  thRefs,
  thisKey,
  rightKey,
  onResizePair,
}) => {
  const registerRef = (node) => {
    thRefs.current[thisKey] = node;
  };
  const onResizeMouseDown = useAdjacentColumnResize(thRefs, thisKey, rightKey, onResizePair);

  return (
    <th
      ref={registerRef}
      onClick={onClick}
      style={{
        position: "relative",
        width: width || undefined,
        minWidth: width || undefined,
        maxWidth: width || undefined,
      }}
    >
      {children}
      {rightKey && <ResizeHandle onMouseDown={onResizeMouseDown} />}
    </th>
  );
};

// headers = [{key: "headerKeyForData", text: "Header Text", width?: number}];
// data = [{...}, {...}];
//
// `draggable` (default false) turns on column REORDER (+ resize) — this is
// the Config-mode flag, e.g. `draggable={isConfiguring}`. See
// docs/plans/grid-column-config-feature.md.
// `resizable` (default true) turns on column-width resize alone,
// independent of Config mode, so every consumer can resize columns without
// ever clicking Config — pass `resizable={false}` to opt a screen out and
// keep pre-Config-feature header rendering/behavior exactly as it was. When
// `draggable` is on, resize is always available too regardless of this flag.
const Table = forwardRef(
  (
    {
      headers = [],
      data = [],
      totalRecords = 0,
      selected = false,
      isAPiCallLoading = false,
      onRowClick: onRowClickFn,
      onRowDoubleClick,
      variant = INPUT_VARIANT._,
      style = {},
      pagination = null,
      setPagination,
      noDataMessage = "No Data Found",
      draggable = false,
      resizable = true,
    },
    ref
  ) => {
    const [rowSelection, setRowSelection] = useState({});
    const [sorting, setSorting] = useState([]);
    const [tableState, setTableState] = useState(null);
    const clickTimer = useRef(null);
    // Column key -> <th> DOM node, populated by each header cell's ref —
    // read by useAdjacentColumnResize to get both sides of a divider's
    // current rendered width at drag start (see that hook for why resize is
    // pairwise rather than per-column).
    const thRefs = useRef({});

    // Config (reorder/resize) state — `baseHeaders` is the live column
    // order (only ever diverges from `headers` while actively dragging;
    // reset from `headers` whenever that prop changes), `colWidths` holds
    // any custom widths keyed by column `key` so they render consistently
    // on both header and body cells whether or not Config mode is active.
    const [baseHeaders, setBaseHeaders] = useState(headers);
    const [colWidths, setColWidths] = useState(() =>
      Object.fromEntries(
        headers.filter((h) => h.width).map((h) => [h.key, h.width])
      )
    );
    const [dragOverKey, setDragOverKey] = useState(null);
    // Tracks the column currently being dragged so a floating label can
    // follow the cursor (via DragOverlay below) — mirrors the equivalent
    // behavior already present in common/elements/DataTable, which this
    // component previously lacked (only the drop-target highlight existed).
    const [dragActiveKey, setDragActiveKey] = useState(null);

    useEffect(() => {
      setBaseHeaders(headers);
      setColWidths(
        Object.fromEntries(
          headers.filter((h) => h.width).map((h) => [h.key, h.width])
        )
      );
    }, [headers]);

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { distance: 1 } })
    );

    const onDragStart = ({ active }) => {
      setDragActiveKey(active?.id ?? null);
    };

    const onDragEnd = ({ active, over }) => {
      setDragOverKey(null);
      setDragActiveKey(null);
      if (!active || !over || active.id === over.id) return;

      setBaseHeaders((prev) => {
        const activeIndex = prev.findIndex((h) => h.key === active.id);
        const overIndex = prev.findIndex((h) => h.key === over.id);
        if (activeIndex < 0 || overIndex < 0) return prev;
        return arrayMove(prev, activeIndex, overIndex);
      });
    };

    const onDragOver = ({ over }) => {
      setDragOverKey(over?.id ?? null);
    };

    const activeHeader = baseHeaders.find((h) => h.key === dragActiveKey);

    useImperativeHandle(ref, () => ({
      // Returns the current column order + widths, e.g.
      // [{ key: "miscNumber", width: 350 }, { key: "number", width: 100 }]
      // — used by useGridColumnConfig to persist on "Save Config".
      getColumnLayout: () =>
        baseHeaders.map((h) => ({
          key: h.key,
          width: colWidths[h.key] ?? h.width,
        })),
      // Discards any in-progress drag/resize changes, reverting to what
      // the `headers` prop currently describes — used on "discard".
      resetColumnLayout: () => {
        setBaseHeaders(headers);
        setColWidths(
          Object.fromEntries(
            headers.filter((h) => h.width).map((h) => [h.key, h.width])
          )
        );
      },
    }));

    useEffect(() => {}, [data]);

    useEffect(() => {
      if (pagination !== null) {
        setTableState({
          rowSelection,
          // sorting,
          pagination,
        });
      } else {
        setTableState({
          rowSelection,
          // sorting,
        });
      }
    }, [pagination]);

    useEffect(() => {
      if (selected) {
        setRowSelection({});
      }
    }, [selected]);

    const columns = useMemo(() => {
      return baseHeaders.map(({ key, text }) => {
        // "fullAddress" always stays on one line and clips with an
        // ellipsis rather than wrapping — no hardcoded pixel cap. With no
        // width set yet, nothing bounds the div so it just grows with its
        // content (same as any other column; the table's `responsive`
        // wrapper scrolls horizontally if that makes the row too wide) —
        // nowrap here only stops it from wrapping onto a second line if an
        // outer container happens to squeeze the column narrower than the
        // text. Once a width exists (header-configured default, or a live
        // Config/resize drag), the <td> below is already pixel-bounded to
        // it, so this plain block <div> — no width of its own — naturally
        // fills exactly that (padding-aware) space, and the ellipsis tracks
        // every subsequent resize automatically.
        return {
          accessorKey: key,
          header: text,
          cell: (info) => (
            <div
              style={
                key === "fullAddress"
                  ? {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }
                  : {}
              }
              title={info.getValue()} // show full text on hover
            >
              {info.getValue()}
            </div>
          ),
        };
      });
    }, [baseHeaders, colWidths]);

    const table = useReactTable({
      state: {
        ...tableState,
      },
      rowCount: totalRecords,
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      enableRowSelection: true,
      enableMultiRowSelection: false,
      onRowSelectionChange: setRowSelection,
      // onSortingChange: setSorting,
      enableSorting: false,
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      manualPagination: true,
    });

    const onRowClick = (event, row, tbindex) => {
      onRowClickFn && onRowClickFn(row.original, tbindex);
      row.getToggleSelectedHandler()(event);
    };

    const getHeaderlength = () => {
      const keyWithArray = "headers";
      const totalLength = table.getHeaderGroups()?.reduce((sum, obj) => {
        return (
          sum + (Array.isArray(obj[keyWithArray]) ? obj[keyWithArray].length : 0)
        );
      }, 0);
      return totalLength;
    };

    const changePage = (e) => {
      const page = parseInt(e) - 1;
      table.setPageIndex(page);
      setPagination((prev) => ({ ...prev, pageIndex: page }));
    };

    const tableNode = (
      <>
        {/* {JSON.stringify({ sorting, rowSelection })} */}
        <StyledTable
          striped
          bordered
          responsive
          hover
          className={variant}
          style={style}
        >
          <thead>
            {draggable ? (
              <tr>
                {baseHeaders.map((h, i) => {
                  const rightKey = baseHeaders[i + 1]?.key;
                  return (
                    <ConfigurableHeaderCell
                      key={h.key}
                      id={h.key}
                      width={colWidths[h.key]}
                      isDragOver={dragOverKey === h.key}
                      thRefs={thRefs}
                      rightKey={rightKey}
                      onResizePair={(leftWidth, rightWidth) =>
                        setColWidths((prev) => ({
                          ...prev,
                          [h.key]: leftWidth,
                          ...(rightKey ? { [rightKey]: rightWidth } : {}),
                        }))
                      }
                    >
                      {h.text}
                    </ConfigurableHeaderCell>
                  );
                })}
              </tr>
            ) : (
              table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, i) => {
                    const width = colWidths[header.column.id];
                    const rightKey = headerGroup.headers[i + 1]?.column.id;
                    const headerContent = header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        );

                    return resizable ? (
                      <ResizeOnlyHeaderCell
                        key={header.id}
                        width={width}
                        thRefs={thRefs}
                        thisKey={header.column.id}
                        rightKey={rightKey}
                        onClick={header.column.getToggleSortingHandler()}
                        onResizePair={(leftWidth, rightWidth) =>
                          setColWidths((prev) => ({
                            ...prev,
                            [header.column.id]: leftWidth,
                            ...(rightKey ? { [rightKey]: rightWidth } : {}),
                          }))
                        }
                      >
                        {headerContent}
                      </ResizeOnlyHeaderCell>
                    ) : (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={
                          width ? { width, minWidth: width, maxWidth: width } : undefined
                        }
                      >
                        {headerContent}
                        {/* {{
                          asc: " 🔼",
                          desc: " 🔽",
                        }[header.column.getIsSorted()] ?? null} */}
                      </th>
                    );
                  })}
                </tr>
              ))
            )}
          </thead>
          <tbody>
            {!isAPiCallLoading &&
              table.getRowModel().rows?.length !== 0 &&
              table.getRowModel().rows.map((row, tbindex) => (
                <tr
                  key={row.id}
                  onClick={(event) => {
                    console.log("CLICK EVENT FIRED");

                    if (clickTimer.current) {
                      clearTimeout(clickTimer.current);
                    }

                    clickTimer.current = setTimeout(() => {
                      console.log("SINGLE CLICK EXECUTED");
                      onRowClick(event, row, tbindex);
                    }, 250); // wait to see if double click happens
                  }}
                  onDoubleClick={() => {
                    console.log("Double CLICK EVENT FIRED");
                    if (clickTimer.current) {
                      clearTimeout(clickTimer.current);
                    }

                    if (onRowDoubleClick) {
                      onRowDoubleClick(row.original, tbindex);
                    }
                  }}
                  className={rowSelection[tbindex] && "selected"}
                >
                  {row.getVisibleCells().map((cell) => {
                    const width = colWidths[cell.column.id];
                    return (
                      <td
                        key={cell.id}
                        style={
                          width ? { width, minWidth: width, maxWidth: width } : undefined
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}

            {!isAPiCallLoading && table.getRowModel().rows?.length === 0 && (
              <NoDataFound>
                <td colSpan={getHeaderlength()}>{noDataMessage}</td>
              </NoDataFound>
            )}
            {isAPiCallLoading && (
              <Loading>
                <td colSpan={getHeaderlength()}>
                  <Loader themeMode="light" />
                </td>
              </Loading>
            )}
          </tbody>
        </StyledTable>
        {!isAPiCallLoading && totalRecords !== 0 && (
          <Pagination>
            <PaginationInfo>
              <span>
                Showing{" "}
                {/* {totalRecords > table.getState().pagination.pageSize &&
                  (pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize -
                    table.getRowModel().rows.length.toLocaleString() +
                    1 +
                    " - "} */}
                {totalRecords > table.getState().pagination.pageSize &&
                  table.getState().pagination.pageSize * pagination.pageIndex +
                    1 +
                    " - "}
                {/* {(pagination.pageIndex + 1) *
                  table.getRowModel().rows.length.toLocaleString()} */}
                {totalRecords -
                  table.getState().pagination.pageSize * pagination.pageIndex <
                table.getState().pagination.pageSize
                  ? totalRecords
                  : table.getState().pagination.pageSize * pagination.pageIndex +
                    table.getState().pagination.pageSize * 1}{" "}
                of {table.getRowCount().toLocaleString()} Rows{" "}
              </span>
              <span className="flex items-center gap-1">
                | Page
                <span className="ps-1">
                  {table.getState().pagination.pageIndex + 1} of{" "}
                  {table.getPageCount().toLocaleString()}
                </span>
              </span>
            </PaginationInfo>
            {pagination !== null &&
            table.getState().pagination.pageSize + table.getRowCount() >=
              table.getState().pagination.pageSize ? (
              <>
                <PaginationRowLimit>
                  <span className="per-page-text">per page</span>
                  <Select
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                      table.setPageSize(Number(e));
                      changePage(1);
                    }}
                    className="per-page-select"
                    variant="outlined"
                    options={[
                      { value: 10, label: "10" },
                      { value: 25, label: "25" },
                      { value: 50, label: "50" },
                      { value: 100, label: "100" },
                    ]}
                  ></Select>
                  <span className="records-text">records</span>
                </PaginationRowLimit>
                <PaginationControls>
                  <PaginationButtonGroup>
                    <Button
                      className="btn-page-first"
                      onClick={() => table.firstPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <span style={{ display: "flex" }}>
                        <LeftOutlined style={{ marginRight: "-5px" }} />
                        <LeftOutlined />
                      </span>
                    </Button>
                    <Button
                      className="btn-page-prev"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      <LeftOutlined />
                    </Button>
                    <Button
                      className="btn-page-next"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <RightOutlined />
                    </Button>
                    <Button
                      className="btn-page-last"
                      onClick={() => table.lastPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      <span style={{ display: "flex" }}>
                        <RightOutlined style={{ marginRight: "-5px" }} />
                        <RightOutlined />
                      </span>
                    </Button>
                  </PaginationButtonGroup>
                  <InputPageChange>
                    <div className="d-flex align-items-baseline">
                      <span className="me-2 go-page-text">| Go to page:</span>
                      <span>
                        <Select
                          variant="outlined"
                          value={pagination.pageIndex + 1}
                          onChange={(e) => {
                            changePage(e);
                          }}
                          disabled={totalRecords < table.getPageCount()}
                          className="go-page-select"
                          options={generateArray(table.getPageCount())?.map(
                            (a) => {
                              return {
                                label: a,
                                value: a,
                              };
                            }
                          )}
                        ></Select>
                      </span>
                    </div>
                  </InputPageChange>
                </PaginationControls>
              </>
            ) : (
              ""
            )}
          </Pagination>
        )}
      </>
    );

    if (!draggable) {
      return tableNode;
    }

    return (
      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={baseHeaders.map((h) => h.key)}
          strategy={horizontalListSortingStrategy}
        >
          {tableNode}
        </SortableContext>

        {/* Floating label following the cursor while a column header is
            being dragged — styled to match common/elements/DataTable's
            equivalent DragOverlay for visual consistency across both grid
            primitives. */}
        <DragOverlay>
          {activeHeader ? (
            <th
              style={{
                backgroundColor: "#76C4E8",
                padding: 8,
                border: "1px solid #24709d",
                borderRadius: 4,
                boxShadow: "0px 5px 15px 0px rgba(0, 0, 0, 0.5)",
              }}
            >
              {activeHeader.text}
            </th>
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  }
);

export default Table;
